import { useContext } from 'react';
import PropTypes from 'prop-types';
import ProductsContext from '../contexts/ProductsContext.js';
import styles from './PageProductComparison.module.css';
import pageProductComparisonData from './PageProductComparison.json';
import classNames from 'classnames';
import LinkAuthFlow from './LinkAuthFlow.js';
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
import IconCheckmark from './IconCheckmark.js';
import deriveProductFromProductSku from '../utils/deriveProductFromProductSku.js';
import formatCallToActionText from '../utils/formatCallToActionText.js';

const { products, features, disclaimer } = pageProductComparisonData;

function PageProductComparison() {
  const productsContext = useContext(ProductsContext);

  const {
    productSku,
    bestValueProductSku,
    productSkuComparisonOrderCSV: productCompareCSV,
  } = useSearchParamOrDefaultProps();

  const productComparisonList = [
    productSku,

    ...productCompareCSV
      .split(',')
      .map((productId) => productId.trim()) // Handle "foo, bar"
      // Handle "foo, ";
      // Primary product always goes first, remove duplicate
      .filter((productId) => {
        return (
          // Exclude empty strings
          productId &&
          // Exclude primary product since it's always added to position 1
          productId !== productSku &&
          // Exclude products we do not have data for
          Object.keys(productsContext).includes(productId)
        );
      }),
  ];

  const productsList = products.filter(({ resourceId }) =>
    productComparisonList.includes(resourceId),
  );

  const productsOrderedByProductComparisonList = productsList.sort(
    ({ resourceId: aResourceId }, { resourceId: bResourceId }) => {
      return (
        productComparisonList.indexOf(aResourceId) -
        productComparisonList.indexOf(bResourceId)
      );
    },
  );

  const featuresOrderedByProductOffering = [...features].sort(
    (aFeature, bFeature) => {
      return Number(bFeature[productSku]) - Number(aFeature[productSku]);
    },
  );

  return (
    <section className={styles.PageProductComparison}>
      <div className={styles.tableWrapper}>
        <table
          className={classNames(styles.table, {
            [styles.cnbcPlus]: productSku === 'Plus',
          })}
        >
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} ${styles.thFirstColumnSpacer}`}></th>

              {productsOrderedByProductComparisonList.map(
                ({ resourceId, displayName, highlightColor }, i) => {
                  const isProductBestValue = resourceId === bestValueProductSku;
                  const isPrimaryProductRegisteredOnly =
                    productSku === 'RegisteredOnly';
                  const isCurrentProductRegisteredOnly =
                    resourceId === 'RegisteredOnly';

                  const product = productsContext[resourceId];
                  const { rate_plans: ratePlans } = product;

                  const yearlyRatePlan =
                    ratePlans.find(({ prices }) =>
                      prices.find(({ interval }) => interval === 'year'),
                    ) || {};
                  const monthlyRatePlan =
                    ratePlans.find(({ prices }) =>
                      prices.find(({ interval }) => interval === 'month'),
                    ) || {};

                  const { prices: yearlyRatePlanPrices = [] } = yearlyRatePlan;
                  const [yearlyRatePlanPrice = {}] = yearlyRatePlanPrices;
                  const { amount_usd: yearlyRatePlanPriceAmountUSD = '' } =
                    yearlyRatePlanPrice;

                  const { prices: monthlyRatePlanPrices = [] } =
                    monthlyRatePlan;
                  const [monthlyRatePlanPrice = {}] = monthlyRatePlanPrices;
                  const { amount_usd: monthlyRatePlanPriceAmountUSD = '' } =
                    monthlyRatePlanPrice;

                  const [yearlyDollars, yearlyCents] =
                    yearlyRatePlanPriceAmountUSD.toString().split('.');
                  const [monthlyDollars, monthlyCents] =
                    monthlyRatePlanPriceAmountUSD.toString().split('.');

                  const thProductHeaderStyle = {};
                  thProductHeaderStyle.borderTopColor = highlightColor;

                  return (
                    <th
                      key={displayName + i}
                      className={classNames(styles.th, styles.thHeader, {
                        [styles.thHighlighted]: i === 0,
                      })}
                    >
                      <div
                        className={classNames(styles.thProductHeader, {
                          [styles.thProductHeaderHighlighted]: i === 0,
                        })}
                        style={thProductHeaderStyle}
                      >
                        {isProductBestValue && (
                          <span className={styles.bestValue}>BEST VALUE</span>
                        )}
                        <div
                          className={styles.thProductDisplayName}
                          style={{ color: highlightColor }}
                        >
                          {displayName}
                        </div>

                        <div className={styles.thPriceInfo}>
                          {isCurrentProductRegisteredOnly && (
                            <div className={styles.thProductPriceYearly}>
                              FREE
                            </div>
                          )}

                          {yearlyRatePlanPriceAmountUSD && (
                            <div className={styles.thProductPriceYearly}>
                              ${yearlyDollars}.
                              <sup
                                className={styles.thProductPriceIntervalCents}
                              >
                                {yearlyCents}
                              </sup>
                              <span>/year</span>
                            </div>
                          )}
                          {yearlyRatePlanPriceAmountUSD &&
                            monthlyRatePlanPriceAmountUSD && (
                              <div className={styles.thOr}>or</div>
                            )}
                          {monthlyRatePlanPriceAmountUSD && (
                            <div className={styles.thProductPriceMonthly}>
                              ${monthlyDollars}.
                              <sup
                                className={styles.thProductPriceIntervalCents}
                              >
                                {monthlyCents}
                              </sup>
                              <span> /month</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                  );
                },
              )}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {featuresOrderedByProductOffering.map((feature, i) => {
              const { beforeIconSrc, name: featureName } = feature;
              const isFirstRow = i === 0;
              return (
                <tr
                  key={featureName}
                  className={classNames(styles.tr, {
                    [styles.trSecondRow]: isFirstRow,
                  })}
                >
                  <th className={`${styles.td} ${styles.thFeatureName}`}>
                    <span className={styles.thFeatureNameTextWrapper}>
                      {beforeIconSrc && (
                        <img
                          className={styles.beforeIcon}
                          src={beforeIconSrc}
                        />
                      )}
                      {featureName}
                    </span>
                  </th>
                  {productsOrderedByProductComparisonList.map((product, i) => {
                    const { resourceId: productId } = product;
                    const productHasFeature = feature[productId];
                    const isProductHighlighted = i === 0;
                    const checkMarkColor = isProductHighlighted
                      ? 'var(--featureCheckmarkHighlightedColor)'
                      : 'var(--featureCheckmarkColor)';

                    return (
                      <td
                        key={featureName + productId}
                        className={classNames(
                          `${styles.td} ${styles.tdProductHasFeature}`,
                          {
                            [styles.tdProductHighlighted]: isProductHighlighted,
                          },
                        )}
                      >
                        {productHasFeature ? (
                          <IconCheckmark fill={checkMarkColor} />
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            <tr className={styles.tr}>
              <th className={styles.thCallToActionSpacer}>
                {disclaimer && (
                  <span className={styles.PageProductComparisonTableDisclaimer}>
                    {disclaimer}
                  </span>
                )}
              </th>
              {productsOrderedByProductComparisonList.map(
                ({ callToActionText, callToActionURL, resourceId }, i) => {
                  const isProductHighlighted = i === 0;

                  return (
                    <td
                      className={classNames(styles.tdCallToAction, {
                        [styles.tdCallToActionHighlighted]:
                          isProductHighlighted,
                      })}
                      key={callToActionURL}
                    >
                      <LinkAuthFlow
                        to={callToActionURL}
                        fontWeight={600}
                        boxShadow="0px 2px 4px 0px #00000040"
                        borderStyle="solid"
                        borderWidth="1px"
                        borderRadius="5px"
                        modalType="signUp"
                        contentType="page"
                        className={classNames(styles.link, {
                          [styles.notHighlighted]: !isProductHighlighted,
                        })}
                        analyticFlowPrefix={deriveProductFromProductSku(
                          productSku,
                        )}
                        productSku={resourceId}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="36px"
                        letterSpacing=".5px"
                      >
                        {formatCallToActionText(callToActionText)}
                      </LinkAuthFlow>
                    </td>
                  );
                },
              )}
            </tr>
            <tr>
              <th />
              {productsOrderedByProductComparisonList.map(
                ({ columnDisclaimer, resourceId, index }) => (
                  <td
                    className={styles.tdCallToActionDisclaimer}
                    key={`${resourceId}${index}`}
                  >
                    {columnDisclaimer}
                  </td>
                ),
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default PageProductComparison;
